import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { announcePotContribution } from '../utils/potContribution';
import Header from '../components/Header';
import JackpotPromoBanner from '../components/JackpotPromoBanner';  // eslint-disable-line no-unused-vars -- community pots removed from UI (Jun 2026)
import AllCombosCard from '../components/AllCombosCard';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Ticket, Trophy, History, Loader2, ChevronDown, Star, Zap, Heart, TrendingUp, Flame, Snowflake, ShoppingCart, X, Pencil, Trash2 } from 'lucide-react';
import LotteryPhotoScan from '../components/LotteryPhotoScan';
import LotterySavedSlips from '../components/LotterySavedSlips';
import LotteryBatchScan from '../components/LotteryBatchScan';
import { FreePlayNote, playable } from '../components/FreePlayNote';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ALL_STATES = ['IL', 'MS', 'TN'];
const STATE_NAMES = { IL: 'Illinois', MS: 'Mississippi', TN: 'Tennessee' };
const STATE_COLORS = { IL: '#3B82F6', MS: '#EF4444', TN: '#F59E0B' };

// Small helper — renders a single Pick-N row with colored number balls.
const ResultRow = ({ label, draw }) => {
    const nums = Array.isArray(draw?.winning_numbers) ? draw.winning_numbers : null;
    return (
    <div className="flex items-center gap-1 py-0.5" data-testid={`result-row-${label}`}>
        <span className="text-[9px] font-bold text-[#FFD700] w-8 shrink-0">{label}</span>
        {nums ? (
            <>
                <div className="flex gap-0.5">
                    {nums.map((n, i) => (
                        <span key={i}
                              className="w-4 h-4 sm:w-5 sm:h-5 rounded text-[9px] sm:text-[10px] font-black flex items-center justify-center bg-gradient-to-b from-[#FFD700] to-[#B8860B] text-black shadow">
                            {n}
                        </span>
                    ))}
                </div>
                <span className="text-[8px] text-[#888] ml-auto shrink-0">{draw.draw_date?.slice(5) || ''}</span>
            </>
        ) : (
            <span className="text-[9px] text-[#666]">— pending —</span>
        )}
    </div>
    );
};

const Lottery = () => {
    // (ResultRow defined above)
    const { user, refreshUser } = useAuth();
    const [allowedStates, setAllowedStates] = useState(ALL_STATES);
    const [selectedState, setSelectedState] = useState('IL');
    const [selectedStates, setSelectedStates] = useState(['IL']);
    const [pickType, setPickType] = useState(3);
    const [numbers, setNumbers] = useState([null, null, null]);
    const [betType, setBetType] = useState('straight'); // 'straight' | 'box' | 'straight_box' | 'ways' | 'trips'
    // Inline "Trips" bet — null = all 10 triples (000–999), or a single digit 0-9 for that single triple (e.g. 5 → 555)
    const [tripsDigit, setTripsDigit] = useState(null);

    // Compute "ways" (unique permutation count) for the current digit picks —
    // 3 / 6 for Pick 3 (depending on doubles) and 4 / 6 / 12 / 24 for Pick 4.
    // Triples and quads return 1 (no real "ways" — only a straight is possible).
    const computeWays = (digits) => {
        const filled = digits.filter(d => d !== null && d !== undefined);
        if (filled.length === 0) return 0;
        const counts = {};
        filled.forEach(d => { counts[d] = (counts[d] || 0) + 1; });
        const c = Object.values(counts).sort((a, b) => b - a);
        const k = c.join(',');
        const PICK3 = { '3': 1, '2,1': 3, '1,1,1': 6 };
        const PICK4 = { '4': 1, '3,1': 4, '2,2': 6, '2,1,1': 12, '1,1,1,1': 24 };
        return (filled.length === 3 ? PICK3[k] : PICK4[k]) || 1;
    };
    const [drawingTime, setDrawingTime] = useState('evening');
    // True once the player explicitly taps the draw toggle — stops the smart
    // default from ever overriding a deliberate choice.
    const userPickedDrawRef = useRef(false);
    const [amount, setAmount] = useState('1');
    const [loading, setLoading] = useState(false);
    const [myTickets, setMyTickets] = useState([]);
    const [showTickets, setShowTickets] = useState(false);
    const [showInsights, setShowInsights] = useState(false);
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [lastBet, setLastBet] = useState(null);
    const [favorites, setFavorites] = useState([]);
    const [insights, setInsights] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [showLeaderboard, setShowLeaderboard] = useState(false);
    const [latestDraws, setLatestDraws] = useState([]);
    const [allTriplesLoading, setAllTriplesLoading] = useState(false);
    const [allTriplesAmount, setAllTriplesAmount] = useState('1');
    // null = sentinel for "all 10 selected" (the common case). Set to a Set
    // of digit numbers when the player has hand-picked a subset.
    const [selectedTriples, setSelectedTriples] = useState(() => new Set([0,1,2,3,4,5,6,7,8,9]));
    // State + drawing-time scoped specifically to the All Triples card so it
    // doesn't have to share the parent state selector.
    const [triplesStates, setTriplesStates] = useState(() => new Set(['TN','IL','MS']));
    const [triplesTime, setTriplesTime] = useState('evening'); // midday | evening
    const [selectedDates, setSelectedDates] = useState([]);
    const [lotterySettings, setLotterySettings] = useState(null);
    const [showRules, setShowRules] = useState(false);
    // Bet Slip (cart)
    const [betSlip, setBetSlip] = useState([]);
    const [showSlip, setShowSlip] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [purchaseLoading, setPurchaseLoading] = useState(false);

    // Photo OCR ↔ Saved Slips bridge
    const photoScanRef = useRef(null);
    const [pendingSaveBets, setPendingSaveBets] = useState(null);

    // Edit-ticket modal (pre-cutoff numbers fix)
    const [editTicket, setEditTicket] = useState(null);
    const [editNumbers, setEditNumbers] = useState([]);
    const [editBetType, setEditBetType] = useState('straight');
    const [editSaving, setEditSaving] = useState(false);

    useEffect(() => {
        setNumbers(Array(pickType).fill(null));
    }, [pickType]);

    // Pull allowed states from the backend so per-location toggles take effect.
    useEffect(() => {
        let alive = true;
        axios.get(`${API}/lottery/states`, { withCredentials: true })
            .then(({ data }) => {
                if (!alive) return;
                const allowed = (data?.states || []).filter(s => ALL_STATES.includes(s));
                if (allowed.length > 0) {
                    setAllowedStates(allowed);
                    setSelectedState(prev => allowed.includes(prev) ? prev : allowed[0]);
                    setSelectedStates(prev => {
                        const cleaned = prev.filter(s => allowed.includes(s));
                        return cleaned.length > 0 ? cleaned : [allowed[0]];
                    });
                } else {
                    setAllowedStates([]);
                }
            })
            .catch(() => { /* silent — keep defaults */ });
        return () => { alive = false; };
    }, []);

    // Fetch latest draws and settings on load
    useEffect(() => {
        const fetchLatest = async () => {
            try {
                const [drawsRes, settingsRes] = await Promise.all([
                    axios.get(`${API}/lottery/draws?limit=30`),
                    axios.get(`${API}/lottery/settings`),
                ]);
                setLatestDraws(drawsRes.data);
                setLotterySettings(settingsRes.data);
            } catch {}
        };
        fetchLatest();
        // Load favorites from localStorage
        const saved = localStorage.getItem('lotto_favorites');
        if (saved) setFavorites(JSON.parse(saved));
        // Load last bet from localStorage
        const lb = localStorage.getItem('lotto_last_bet');
        if (lb) setLastBet(JSON.parse(lb));
    }, []);

    const fetchTickets = useCallback(async () => {
        setTicketsLoading(true);
        try {
            const { data } = await axios.get(`${API}/lottery/tickets`);
            setMyTickets(data);
        } catch {}
        setTicketsLoading(false);
    }, []);

    // Auto-fetch ticket history on mount so the banner / counter can populate
    // without waiting for the player to expand the section. Players were
    // missing where their pending bets lived — surface them up-front.
    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    // Anchor for the "X PENDING BETS" banner — clicking the banner scrolls
    // smoothly to the My Tickets list (which is at the bottom of the page).
    const myTicketsRef = useRef(null);
    const pendingTicketCount = useMemo(
        () => myTickets.filter(t => t.status === 'pending').length,
        [myTickets],
    );
    // Once the initial fetch resolves, auto-expand My Tickets if the player
    // has any pending bets so they're immediately visible.
    const didAutoExpandRef = useRef(false);
    useEffect(() => {
        if (didAutoExpandRef.current) return;
        if (pendingTicketCount > 0) {
            setShowTickets(true);
            didAutoExpandRef.current = true;
        }
    }, [pendingTicketCount]);
    const scrollToMyTickets = useCallback(() => {
        setShowTickets(true);
        // Wait a tick for the accordion to expand before scrolling
        setTimeout(() => {
            myTicketsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    }, []);

    // Decide whether a pending ticket is still editable: must be pending AND
    // current Central Time must be before its drawing cutoff.
    const isTicketEditable = useCallback((t) => {
        if (!t || t.status !== 'pending') return false;
        const cutoffs = lotterySettings?.cutoffs || {};
        const cutoffStr = cutoffs[`${t.state}_${t.drawing_time}`] || '11:59 PM';
        // Parse "H:MM AM/PM" → 24h
        const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(cutoffStr.trim());
        if (!m) return false;
        let hh = parseInt(m[1], 10);
        const mm = parseInt(m[2], 10);
        const ap = m[3].toUpperCase();
        if (ap === 'PM' && hh !== 12) hh += 12;
        if (ap === 'AM' && hh === 12) hh = 0;
        // Convert "now" → Central Time
        const nowCT = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
        // Build the cutoff Date object for the ticket's draw_date in CT
        const [y, mo, d] = (t.draw_date || '').split('-').map(Number);
        if (!y || !mo || !d) return false;
        const cutoff = new Date(nowCT.getFullYear(), nowCT.getMonth(), nowCT.getDate(), hh, mm, 0);
        cutoff.setFullYear(y, mo - 1, d);
        return nowCT < cutoff;
    }, [lotterySettings]);

    // Smart default: pre-select the NEXT upcoming draw (midday while we're still
    // before its cutoff, otherwise evening) instead of always defaulting to
    // evening. Prevents morning players from accidentally buying evening tickets.
    // Never overrides a draw the player picked themselves.
    useEffect(() => {
        if (userPickedDrawRef.current) return;
        const cutoffs = lotterySettings?.cutoffs;
        const st = selectedState || (selectedStates && selectedStates[0]);
        if (!cutoffs || !st) return;
        const cutoffStr = cutoffs[`${st}_midday`];
        const m = cutoffStr && /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(cutoffStr.trim());
        if (!m) return;
        let hh = parseInt(m[1], 10); const mm = parseInt(m[2], 10); const ap = m[3].toUpperCase();
        if (ap === 'PM' && hh !== 12) hh += 12;
        if (ap === 'AM' && hh === 12) hh = 0;
        const nowCT = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
        const cutoff = new Date(nowCT.getFullYear(), nowCT.getMonth(), nowCT.getDate(), hh, mm, 0);
        setDrawingTime(nowCT < cutoff ? 'midday' : 'evening');
    }, [lotterySettings, selectedState, selectedStates]);

    const openEditTicket = (t) => {
        setEditTicket(t);
        setEditNumbers([...(t.numbers || [])]);
        setEditBetType(t.bet_type || 'straight');
    };

    const setEditDigit = (idx, val) => {
        const v = parseInt(val, 10);
        if (isNaN(v) || v < 0 || v > 9) return;
        setEditNumbers((prev) => {
            const next = [...prev];
            next[idx] = v;
            return next;
        });
    };

    const submitEditTicket = async () => {
        if (!editTicket) return;
        if (editNumbers.length !== editTicket.pick_type || editNumbers.some(n => n === null || n === undefined)) {
            toast.error(`Pick all ${editTicket.pick_type} digits before saving`);
            return;
        }
        setEditSaving(true);
        try {
            const { data } = await axios.patch(
                `${API}/lottery/ticket/${editTicket.id}`,
                { numbers: editNumbers.map(n => parseInt(n, 10)), bet_type: editBetType },
            );
            toast.success(`Ticket updated → ${data.ticket.numbers.join('-')} (${data.ticket.bet_type})`);
            setEditTicket(null);
            fetchTickets();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not update ticket');
        } finally {
            setEditSaving(false);
        }
    };

    // Player-cancels their own pending ticket before cutoff → wager refunded.
    const [cancelingId, setCancelingId] = useState(null);
    const [replayingId, setReplayingId] = useState(null);    const cancelTicket = async (t) => {
        if (!t || cancelingId) return;
        const label = Array.isArray(t.numbers) ? t.numbers.join('-') : (t.numbers ?? 'this ticket');
        if (!window.confirm(`Cancel ${t.state} ${label}? Your $${Number(t.total_cost ?? t.amount ?? 0).toFixed(2)} wager will be refunded to your credits.`)) return;
        setCancelingId(t.id);
        try {
            const { data } = await axios.post(`${API}/lottery/ticket/${t.id}/cancel`);
            toast.success(`Ticket cancelled — $${Number(data.refunded || 0).toFixed(2)} refunded`);
            fetchTickets();
            if (refreshUser) refreshUser();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not cancel ticket');
        } finally {
            setCancelingId(null);
        }
    };

    const fetchInsights = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/lottery/insights`);
            setInsights(data);
        } catch {}
    }, []);

    const fetchLeaderboard = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/lottery/leaderboard`);
            setLeaderboard(data);
        } catch {}
    }, []);

    const setNumber = (index, value) => {
        const v = parseInt(value);
        if (isNaN(v) || v < 0 || v > 9) return;
        const newNums = [...numbers];
        newNums[index] = v;
        setNumbers(newNums);
    };

    const quickPick = () => {
        setNumbers(Array(pickType).fill(0).map(() => Math.floor(Math.random() * 10)));
    };

    const saveFavorite = () => {
        if (numbers.some(n => n === null)) return;
        const newFav = { numbers: [...numbers], pickType };
        const updated = [...favorites, newFav].slice(-10);
        setFavorites(updated);
        localStorage.setItem('lotto_favorites', JSON.stringify(updated));
        toast.success('Numbers saved to favorites!');
    };

    const loadFavorite = (fav) => {
        setPickType(fav.pickType);
        setNumbers([...fav.numbers]);
    };

    const removeFavorite = (index) => {
        const updated = favorites.filter((_, i) => i !== index);
        setFavorites(updated);
        localStorage.setItem('lotto_favorites', JSON.stringify(updated));
    };

    /**
     * Calculate "to win" amount for a given bet.
     * @param {number} amt - wager amount per drawing per state
     * @param {number} pt - 3 or 4 (Pick type)
     * @param {string} bt - 'straight' | 'box' | 'straight_box'
     * @param {number} numStates - number of states selected (default 1)
     * @param {number} numDays - number of days selected (default 1)
     * @returns {{ perDraw: number, total: number }} max win for a SINGLE matching draw, total wager
     */
    const calcToWin = (amt, pt, bt, numStates = 1, numDays = 1, waysCount = 1) => {
        const payouts = pt === 3
            ? (lotterySettings?.payouts_pick3 || { straight: 600, box: 150, combo: 600, trips: 600 })
            : (lotterySettings?.payouts_pick4 || { straight: 5000, box: 1250, combo: 5000, trips: 5000 });
        if (!amt || amt <= 0) return { perDraw: 0, total: 0 };
        // perDraw = max possible win on ONE drawing (best-case)
        let perDraw;
        if (bt === 'straight') perDraw = amt * payouts.straight;
        else if (bt === 'box') perDraw = amt * payouts.box;
        else if (bt === 'ways') perDraw = amt * (payouts.combo ?? payouts.straight); // one of N perms wins at the COMBO rate
        else if (bt === 'trips') perDraw = amt * (payouts.trips ?? payouts.straight); // any single triple hit pays at the TRIPS rate
        else /* straight_box */ perDraw = amt * (payouts.straight + payouts.box);
        // Total potential = perDraw * states * days
        return { perDraw, total: perDraw * numStates * numDays };
    };

    const addToSlip = () => {
        if (numbers.some(n => n === null)) {
            toast.error('Please select all numbers');
            return;
        }
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) return;
        const dates = selectedDates.length > 0 ? [...selectedDates] : undefined;

        // Expand bet types — "straight_box" = both straight + box on the same number
        const expandedBetTypes = betType === 'straight_box' ? ['straight', 'box'] : [betType];
        // Expand states (multi-select). Fall back to single legacy state if list is empty.
        const states = (selectedStates && selectedStates.length > 0) ? selectedStates : [selectedState];

        if (editingIndex !== null) {
            // Editing existing slip item — keep single-state/single-bet semantics for the editor
            const updated = [...betSlip];
            updated[editingIndex] = {
                state: states[0], pickType, numbers: [...numbers],
                betType: expandedBetTypes[0], amount: amt, drawingTime, dates,
            };
            setBetSlip(updated);
            setEditingIndex(null);
            toast.success('Bet updated');
        } else {
            // Fan out: one slip line per (state × betType)
            const newItems = [];
            for (const st of states) {
                for (const bt of expandedBetTypes) {
                    newItems.push({
                        state: st, pickType, numbers: [...numbers],
                        betType: bt, amount: amt, drawingTime, dates,
                    });
                }
            }
            setBetSlip(prev => [...prev, ...newItems]);
            toast.success(
                newItems.length === 1
                    ? 'Added to slip'
                    : `Added ${newItems.length} bets to slip`
            );
        }
        setNumbers(Array(pickType).fill(null));
        setSelectedDates([]);
        setShowSlip(true);

        // Save the user's setup (pre-expansion) for one-tap "Repeat last bet"
        const setup = {
            states: [...states],
            pickType,
            numbers: [...numbers],
            betType, // can be 'straight' | 'box' | 'straight_box'
            amount: amt,
            drawingTime,
        };
        setLastSetup(setup);
        try { localStorage.setItem('lotto_last_setup', JSON.stringify(setup)); } catch { /* ignore */ }
    };

    const editSlipItem = (index) => {
        const item = betSlip[index];
        setSelectedState(item.state);
        setSelectedStates([item.state]);
        setPickType(item.pickType);
        setNumbers([...item.numbers]);
        setBetType(item.betType);
        setAmount(String(item.amount));
        setDrawingTime(item.drawingTime);
        userPickedDrawRef.current = true; // preserve the edited item's draw
        setSelectedDates(item.dates || []);
        setEditingIndex(index);
        setShowSlip(false);
        // Scroll to top
        window.scrollTo({ top: 300, behavior: 'smooth' });
    };

    const removeSlipItem = (index) => {
        setBetSlip(prev => prev.filter((_, i) => i !== index));
        if (editingIndex === index) setEditingIndex(null);
    };

    const clearSlip = () => {
        setBetSlip([]);
        setEditingIndex(null);
    };

    const getSlipTotal = () => {
        return betSlip.reduce((sum, item) => {
            const days = item.dates?.length || 1;
            return sum + (item.amount * days);
        }, 0);
    };

    const purchaseAll = async () => {
        if (betSlip.length === 0) return;
        setPurchaseLoading(true);
        let successCount = 0;
        let failCount = 0;
        let aggregate = null;
        for (const item of betSlip) {
            try {
                // "Ways" bet type fans out into N straight permutations via
                // the dedicated endpoint (e.g. 1-2-3 → 6 straight tickets).
                const isWays = item.betType === 'ways';
                const endpoint = isWays ? `${API}/lottery/ticket/ways` : `${API}/lottery/ticket`;
                const { data } = await axios.post(endpoint, {
                    state: item.state, pick_type: item.pickType, numbers: item.numbers,
                    bet_type: isWays ? 'straight' : item.betType,
                    amount: item.amount, drawing_time: item.drawingTime,
                    dates: item.dates,
                });
                successCount++;
                const c = data?.pot_contribution;
                if (c) {
                    aggregate = aggregate
                        ? {
                            grand_add: aggregate.grand_add + (c.grand_add || 0),
                            super_add: aggregate.super_add + (c.super_add || 0),
                            blake_add: aggregate.blake_add + (c.blake_add || 0),
                            total_add: aggregate.total_add + (c.total_add || 0),
                            super_active: aggregate.super_active || c.super_active,
                        }
                        : { ...c };
                }
            } catch (e) {
                toast.error(e.response?.data?.detail || `Failed: ${item.numbers.join('-')}`);
                failCount++;
            }
        }
        if (successCount > 0) {
            toast.success(`${successCount} ticket(s) purchased!`);
            const lastItem = betSlip[betSlip.length - 1];
            setLastBet({ state: lastItem.state, pickType: lastItem.pickType, numbers: lastItem.numbers, betType: lastItem.betType, amount: lastItem.amount, drawingTime: lastItem.drawingTime });
            localStorage.setItem('lotto_last_bet', JSON.stringify(lastItem));
            setBetSlip([]);
            setShowSlip(false);
            if (refreshUser) refreshUser();
            announcePotContribution(aggregate);
        }
        setPurchaseLoading(false);
    };

    const [lastSetup, setLastSetup] = useState(null); // { states, pickType, numbers, betType, amount, drawingTime }
    const [autobuy, setAutobuy] = useState({ enabled: false, config: null, last_run_date: null });
    const [autobuyLoading, setAutobuyLoading] = useState(false);

    const fetchAutobuy = async () => {
        try {
            const { data } = await axios.get(`${API}/lottery/autobuy`);
            setAutobuy(data);
        } catch { /* ignore */ }
    };

    useEffect(() => { fetchAutobuy(); }, []);

    const enableAutobuy = async () => {
        if (numbers.some(n => n === null)) {
            toast.error('Select all your numbers first');
            return;
        }
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) {
            toast.error('Enter a valid amount first');
            return;
        }
        const states = (selectedStates && selectedStates.length > 0) ? selectedStates : [selectedState];
        setAutobuyLoading(true);
        try {
            await axios.post(`${API}/lottery/autobuy`, {
                states,
                pick_type: pickType,
                numbers,
                bet_type: betType,
                amount: amt,
                drawing_time: drawingTime,
            });
            toast.success('Auto-buy enabled — runs daily before cutoff');
            await fetchAutobuy();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to enable autobuy');
        }
        setAutobuyLoading(false);
    };

    const disableAutobuy = async () => {
        setAutobuyLoading(true);
        try {
            await axios.delete(`${API}/lottery/autobuy`);
            toast.success('Auto-buy disabled');
            await fetchAutobuy();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
        setAutobuyLoading(false);
    };

    useEffect(() => {
        try {
            const raw = localStorage.getItem('lotto_last_setup');
            if (raw) setLastSetup(JSON.parse(raw));
        } catch { /* ignore */ }
    }, []);

    const repeatLastSetup = () => {
        if (!lastSetup) return;
        const { states, pickType: pt, numbers: nums, betType: bt, amount: amt, drawingTime: dt } = lastSetup;
        if (Array.isArray(states) && states.length > 0) {
            setSelectedStates(states);
            setSelectedState(states[0]);
        }
        if (pt) setPickType(pt);
        if (Array.isArray(nums)) setNumbers([...nums]);
        if (bt) setBetType(bt);
        if (amt !== undefined) setAmount(String(amt));
        if (dt) { setDrawingTime(dt); userPickedDrawRef.current = true; }
        setSelectedDates([]);
        setEditingIndex(null);
        toast.success('Last setup restored — review and add to slip');
    };

    const handleQuickReplay = async () => {
        if (!lastBet) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/lottery/ticket`, {
                state: lastBet.state, pick_type: lastBet.pickType, numbers: lastBet.numbers,
                bet_type: lastBet.betType, amount: lastBet.amount, drawing_time: lastBet.drawingTime,
            });
            toast.success(`Replayed: ${data.numbers.join('-')} — $${lastBet.amount}`);
            if (refreshUser) refreshUser();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Replay failed');
        }
        setLoading(false);
    };

    // Re-buy any past play straight from history. Re-places the SAME state /
    // pick / numbers / bet type / amount / drawing window for the next upcoming
    // draw (backend defaults draw_date to today CT). Standard Pick 3/4 plays
    // only — bulk trips/combos are excluded.
    const replayTicket = async (t) => {
        if (!Array.isArray(t.numbers)) return;
        setReplayingId(t.id);
        try {
            const { data } = await axios.post(`${API}/lottery/ticket`, {
                state: t.state, pick_type: t.pick_type, numbers: t.numbers,
                bet_type: t.bet_type, amount: t.amount, drawing_time: t.drawing_time,
            });
            toast.success(`Replayed ${t.state} ${data.numbers.join('-')} (${t.bet_type}) — $${Number(t.amount).toFixed(2)}`);
            fetchTickets();
            if (refreshUser) refreshUser();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Replay failed — the draw may be past cutoff');
        }
        setReplayingId(null);
    };


    const placeInlineTrips = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) {
            toast.error('Pick a wager amount first');
            return;
        }
        const states = (selectedStates && selectedStates.length > 0) ? selectedStates : [selectedState];
        if (states.length === 0) {
            toast.error('Pick at least one state');
            return;
        }
        const digits = tripsDigit === null ? [0,1,2,3,4,5,6,7,8,9] : [tripsDigit];
        const dates = selectedDates.length > 0 ? [...selectedDates] : undefined;

        const totalTickets = digits.length * states.length * (dates?.length || 1);
        const total = amt * totalTickets;
        const triplesLabel = digits.map(d => `${d}${d}${d}`).join(', ');
        const stateLabel = states.length >= 3 ? 'ALL STATES' : states.join(', ');
        if (!window.confirm(
            `Place ${digits.length === 10 ? 'ALL TRIPLES' : `Triple ${triplesLabel}`} on ${stateLabel} (${drawingTime})?\n\n` +
            `$${amt.toFixed(2)} × ${digits.length} triple${digits.length === 1 ? '' : 's'} × ${states.length} state${states.length === 1 ? '' : 's'}${dates ? ` × ${dates.length} day${dates.length === 1 ? '' : 's'}` : ''} = ${totalTickets} tickets\n` +
            `Total cost: $${total.toFixed(2)}`
        )) return;

        setLoading(true);
        let aggregate = null;
        let placedAny = false;
        try {
            const dateList = dates && dates.length > 0 ? dates : [null];
            for (const dt of dateList) {
                const payload = { states, amount: amt, drawing_time: drawingTime, digits };
                if (dt) payload.dates = [dt];
                const { data } = await axios.post(`${API}/lottery/all-triples`, payload);
                placedAny = true;
                const c = data?.pot_contribution;
                if (c) {
                    aggregate = aggregate
                        ? {
                            grand_add: aggregate.grand_add + (c.grand_add || 0),
                            super_add: aggregate.super_add + (c.super_add || 0),
                            blake_add: aggregate.blake_add + (c.blake_add || 0),
                            total_add: aggregate.total_add + (c.total_add || 0),
                            super_active: aggregate.super_active || c.super_active,
                        }
                        : { ...c };
                }
            }
            if (placedAny) {
                toast.success(
                    digits.length === 10
                        ? `All 10 triples placed across ${states.join(', ')} — $${total.toFixed(2)} total. Hit any triple to win big!`
                        : `Triple ${triplesLabel} placed across ${states.join(', ')} — $${total.toFixed(2)} total.`
                );
                setSelectedDates([]);
                if (refreshUser) refreshUser();
                announcePotContribution(aggregate);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Trips bet failed');
        }
        setLoading(false);
    };

    const handleAllTriples = async () => {
        const amt = parseFloat(allTriplesAmount);
        if (!amt || amt <= 0) {
            toast.error('Pick an amount per triple');
            return;
        }
        const digits = Array.from(selectedTriples).sort((a, b) => a - b);
        if (digits.length === 0) {
            toast.error('Pick at least one triple to play');
            return;
        }
        const states = Array.from(triplesStates);
        if (states.length === 0) {
            toast.error('Pick at least one state');
            return;
        }
        const totalTickets = digits.length * states.length;
        const total = amt * totalTickets;
        const triplesLabel = digits.map(d => `${d}${d}${d}`).join(', ');
        const stateLabel = states.length === 3 ? 'ALL 3 STATES' : states.join(', ');
        if (!window.confirm(
            `Place straight triples on ${stateLabel} (${triplesTime}) drawing?\n\n` +
            `$${amt.toFixed(2)} on each of: ${triplesLabel}\n` +
            `${digits.length} triple${digits.length === 1 ? '' : 's'} × ${states.length} state${states.length === 1 ? '' : 's'} = ${totalTickets} tickets\n` +
            `Total cost: $${total.toFixed(2)}`
        )) return;
        setAllTriplesLoading(true);
        try {
            const { data } = await axios.post(`${API}/lottery/all-triples`, {
                states, amount: amt, drawing_time: triplesTime, digits,
            });
            toast.success(
                `${data.tickets_placed} tickets placed across ${data.states.join(', ')} — total $${data.total_cost}. Win $${data.payout_per_hit}/hit.`
            );
            if (refreshUser) refreshUser();
            announcePotContribution(data?.pot_contribution);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'All Triples failed');
        }
        setAllTriplesLoading(false);
    };

    const toggleTriple = (d) => {
        setSelectedTriples(prev => {
            const next = new Set(prev);
            if (next.has(d)) next.delete(d); else next.add(d);
            return next;
        });
    };
    const selectAllTriples = () => setSelectedTriples(new Set([0,1,2,3,4,5,6,7,8,9]));
    const clearAllTriples = () => setSelectedTriples(new Set());

    const toggleTriplesState = (s) => {
        setTriplesStates(prev => {
            const next = new Set(prev);
            if (next.has(s)) next.delete(s); else next.add(s);
            return next;
        });
    };

    // Get latest draws for each state (optionally filtered by drawing time)
    const getLatestForState = (state, pt, drawingTimeFilter = null) => {
        return latestDraws.find(d =>
            d.state === state &&
            d.pick_type === pt &&
            (!drawingTimeFilter || d.drawing_time === drawingTimeFilter)
        );
    };

    return (
        <div className="a11y-font min-h-screen bg-[#050505]">
            <Header />
            {/* Community pots (Community Grand / BEToBuck$) removed from UI (Jun 2026) */}
            {/* <JackpotPromoBanner /> */}
            <div className="max-w-2xl mx-auto px-4 py-6">
                {/* Title */}
                <div className="text-center mb-4">
                    <h1 className="text-3xl font-black text-[#FFD700]">LOTTO</h1>
                    <p className="text-[#A0AAB2] text-sm">Pick your numbers, win big</p>
                </div>

                {/* Balance */}
                <div className="card-dark p-3 mb-4 flex items-center justify-between">
                    <span className="text-xs text-[#A0AAB2]">YOUR BALANCE</span>
                    <span className="text-right"><span className="text-xl font-black text-[#FFD700] font-mono" data-testid="lottery-balance">{playable(user).toFixed(2)}</span><FreePlayNote user={user} testId="lottery-free-play" /></span>
                </div>

                {/* Pending bets banner — auto-shown when player has open tickets so
                    they always have a clear path back to view what's in flight. */}
                {pendingTicketCount > 0 && (
                    <button
                        type="button"
                        onClick={scrollToMyTickets}
                        data-testid="pending-bets-banner"
                        className="w-full mb-4 rounded-xl border-2 border-[#FFD700]/40 bg-gradient-to-r from-[#FFD700]/15 via-[#FF6B35]/10 to-[#FFD700]/5 px-4 py-3 flex items-center justify-between gap-3 hover:from-[#FFD700]/25 hover:to-[#FFD700]/10 active:scale-[0.99] transition-all"
                    >
                        <div className="flex items-center gap-3">
                            <span className="relative flex h-3 w-3">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FFD700] opacity-60" />
                                <span className="relative inline-flex h-3 w-3 rounded-full bg-[#FFD700]" />
                            </span>
                            <div className="text-left">
                                <p className="text-[#FFD700] font-black text-base leading-tight">
                                    {pendingTicketCount} PENDING BET{pendingTicketCount === 1 ? '' : 'S'}
                                </p>
                                <p className="text-[10px] text-[#A0AAB2]">Tap to view tickets awaiting tonight&apos;s draw</p>
                            </div>
                        </div>
                        <ChevronDown className="w-5 h-5 text-[#FFD700]" />
                    </button>
                )}

                {/* Quick Replay */}
                {lastBet && (
                    <div className="card-dark p-3 mb-4" data-testid="quick-replay">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-bold text-white flex items-center gap-1"><Zap className="w-3 h-3 text-[#39FF14]" /> Quick Replay</p>
                                <p className="text-[10px] text-[#A0AAB2]">
                                    Last bet: {lastBet.numbers.join('-')} — {STATE_NAMES[lastBet.state]} {lastBet.drawingTime} {lastBet.betType} — ${lastBet.amount}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button size="sm" onClick={handleQuickReplay} disabled={loading}
                                    className="bg-[#39FF14] text-black text-xs font-bold h-8" data-testid="replay-btn">
                                    <Zap className="w-3 h-3 mr-1" /> Play Again
                                </Button>
                                <Button size="sm" variant="outline" onClick={saveFavorite}
                                    className="border-[#FF007F]/30 text-[#FF007F] h-8 text-xs" data-testid="save-fav-btn">
                                    <Heart className="w-3 h-3" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* PHOTO OCR — scan handwritten slip + saved slips library */}
                {['admin', 'cashier', 'operator', 'distributor'].includes(user?.role) ? (
                    <div className="mb-4" data-testid="lottery-staff-scan-section">
                        <div className="mb-3 rounded-xl border border-[#39FF14]/40 bg-[#39FF14]/5 px-4 py-3">
                            <p className="text-sm font-black text-[#39FF14]">Staff ticket scanner</p>
                            <p className="text-[11px] text-[#A0AAB2]">
                                You're signed in as staff — scan customers' slips here and each ticket is logged to their account (not yours).
                            </p>
                        </div>
                        <LotteryBatchScan
                            playersEndpoint={user?.role === 'admin' ? '/admin/users' : '/cashier/players'}
                            onPlaced={() => { fetchTickets(); if (refreshUser) refreshUser(); }}
                        />
                    </div>
                ) : (
                    <>
                        <div className="mb-4" data-testid="lottery-photo-scan-section">
                            <LotteryPhotoScan
                                ref={photoScanRef}
                                onSubmitted={() => { fetchTickets(); if (refreshUser) refreshUser(); }}
                                onLastBatch={(bets) => setPendingSaveBets(bets)}
                            />
                        </div>
                        <LotterySavedSlips
                            onReplay={(bets, label) => photoScanRef.current?.replayBets(bets, label)}
                            pendingSaveBets={pendingSaveBets}
                            onPendingSaveConsumed={() => setPendingSaveBets(null)}
                        />
                    </>
                )}

                {/* THE BIG 3 removed (Jun 2026) — retired per operator; historical
                    big3 tickets still settle/display in admin. */}

                {/* ALL TRIPLES — one-click card. Only shown when a Pick 3 state is selected
                    since triples are a Pick-3-only concept. */}
                {pickType === 3 && (
                <div className="mb-4 rounded-2xl border-2 border-[#FFD700]/40 bg-gradient-to-br from-[#FFD700]/10 to-amber-900/10 overflow-hidden" data-testid="all-triples-card">
                    <div className="p-4 text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                            <Zap className="w-4 h-4 text-[#FFD700]" />
                            <h2 className="text-base font-black text-white tracking-wide">ALL TRIPLES</h2>
                            <Zap className="w-4 h-4 text-[#FFD700]" />
                        </div>
                        <span className="inline-block px-2 py-0.5 rounded-full bg-[#FFD700]/20 text-[#FFD700] text-[10px] font-bold mb-2">
                            ONE CLICK · COVERS 000–999
                        </span>
                        <p className="text-xs text-[#A0AAB2] mb-3">
                            Pick triples + states to bet straight across the daily Pick 3 drawings.
                            If any picked triple drops in any chosen state, you win.
                        </p>

                        {/* State chips — multi-select, all 3 selected by default */}
                        <div className="flex items-center justify-center gap-2 mb-3" data-testid="all-triples-states">
                            <span className="text-[10px] uppercase text-[#A0AAB2] font-bold">States:</span>
                            {['TN', 'IL', 'MS'].map(s => {
                                const active = triplesStates.has(s);
                                return (
                                    <button key={s} type="button"
                                        onClick={() => toggleTriplesState(s)}
                                        className={`px-3 py-1 rounded-md text-xs font-bold border transition ${
                                            active
                                                ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                                : 'bg-black/40 text-[#FFD700]/60 border-[#FFD700]/30 hover:border-[#FFD700]/70'
                                        }`}
                                        data-testid={`triples-state-${s}`}
                                        aria-pressed={active}>
                                        {s}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Drawing-time toggle — midday / evening only */}
                        <div className="flex items-center justify-center gap-2 mb-3" data-testid="all-triples-time">
                            <span className="text-[10px] uppercase text-[#A0AAB2] font-bold">Drawing:</span>
                            {['midday', 'evening'].map(t => {
                                const active = triplesTime === t;
                                return (
                                    <button key={t} type="button"
                                        onClick={() => setTriplesTime(t)}
                                        className={`px-3 py-1 rounded-md text-xs font-bold border transition capitalize ${
                                            active
                                                ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                                : 'bg-black/40 text-[#FFD700]/60 border-[#FFD700]/30 hover:border-[#FFD700]/70'
                                        }`}
                                        data-testid={`triples-time-${t}`}
                                        aria-pressed={active}>
                                        {t}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex items-center justify-center gap-2 mb-2">
                            <span className="text-[10px] uppercase text-[#A0AAB2] font-bold">Per Triple</span>
                            <select value={allTriplesAmount}
                                onChange={e => setAllTriplesAmount(e.target.value)}
                                className="h-9 px-3 rounded-md bg-black/50 border border-[#FFD700]/40 text-[#FFD700] text-sm font-bold font-mono"
                                data-testid="all-triples-amount">
                                {['0.50','1','2','5','10','20'].map(v => <option key={v} value={v}>${v}</option>)}
                            </select>
                            <span className="text-[#A0AAB2] text-xs">×{selectedTriples.size}×{triplesStates.size}</span>
                            <span className="text-[#A0AAB2]">=</span>
                            <span className="text-white font-mono font-black">${(parseFloat(allTriplesAmount || 0) * selectedTriples.size * triplesStates.size).toFixed(2)}</span>
                        </div>
                        {/* All / None toggle row */}
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <button type="button" onClick={selectAllTriples}
                                className={`px-2.5 py-1 rounded text-[10px] font-bold border transition ${
                                    selectedTriples.size === 10
                                        ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                        : 'bg-black/40 text-[#FFD700] border-[#FFD700]/40 hover:bg-[#FFD700]/10'
                                }`}
                                data-testid="all-triples-select-all">
                                All 10
                            </button>
                            <button type="button" onClick={clearAllTriples}
                                className="px-2.5 py-1 rounded text-[10px] font-bold border bg-black/40 text-[#A0AAB2] border-white/10 hover:text-white"
                                data-testid="all-triples-clear">
                                Clear
                            </button>
                            <span className="text-[10px] text-[#A0AAB2] ml-1">
                                {selectedTriples.size}/10 selected
                            </span>
                        </div>
                        <div className="grid grid-cols-5 sm:grid-cols-10 gap-1 mb-3" data-testid="all-triples-chips">
                            {[0,1,2,3,4,5,6,7,8,9].map(d => {
                                const active = selectedTriples.has(d);
                                return (
                                    <button key={d} type="button"
                                        onClick={() => toggleTriple(d)}
                                        className={`text-center py-2 rounded text-xs font-mono font-bold border transition ${
                                            active
                                                ? 'bg-[#FFD700] text-black border-[#FFD700] shadow-md shadow-[#FFD700]/30'
                                                : 'bg-black/40 text-[#FFD700]/50 border-[#FFD700]/20 hover:border-[#FFD700]/60 hover:text-[#FFD700]'
                                        }`}
                                        data-testid={`triple-chip-${d}`}
                                        aria-pressed={active}>
                                        {d}{d}{d}
                                    </button>
                                );
                            })}
                        </div>
                        <Button onClick={handleAllTriples}
                            disabled={allTriplesLoading || selectedTriples.size === 0 || triplesStates.size === 0}
                            className="w-full bg-gradient-to-r from-[#FFD700] to-amber-500 text-black font-black py-4 text-base rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="play-all-triples-btn">
                            {allTriplesLoading
                                ? <Loader2 className="w-5 h-5 animate-spin" />
                                : selectedTriples.size === 0
                                    ? 'Pick at least one triple'
                                    : triplesStates.size === 0
                                        ? 'Pick at least one state'
                                        : <>PLAY {selectedTriples.size * triplesStates.size} TICKET{selectedTriples.size * triplesStates.size === 1 ? '' : 'S'} — ${(parseFloat(allTriplesAmount || 0) * selectedTriples.size * triplesStates.size).toFixed(2)}</>}
                        </Button>
                    </div>
                </div>
                )}

                {/* 1-click "All Combos" — plays every prior-day winning number
                    as a combo (ways) across the selected states. */}
                <AllCombosCard
                    allowedStates={allowedStates}
                    onPurchased={() => {
                        // Refresh bet history so the player sees the new tickets
                        // pop up immediately.
                        fetchTickets();
                    }}
                />

                {/* Latest Winning Numbers */}
                <div className="card-dark p-3 mb-4" data-testid="latest-winning-numbers"
                     style={{ borderLeft: '3px solid #FFD700' }}>
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
                            LATEST WINNING NUMBERS
                        </h3>
                        <span className="text-[9px] text-[#A0AAB2] uppercase tracking-wider">auto-updated</span>
                    </div>
                    <div className="space-y-2">
                        {allowedStates.map(st => (
                            <div key={st} className="rounded-lg p-2"
                                 style={{
                                     background: 'rgba(255,255,255,0.03)',
                                     borderLeft: `3px solid ${STATE_COLORS[st]}`,
                                 }}
                                 data-testid={`state-results-${st}`}>
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[11px] font-black" style={{ color: STATE_COLORS[st] }}>
                                        {STATE_NAMES[st]}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-1.5">
                                    {['midday', 'evening'].map(dt => {
                                        const p3 = getLatestForState(st, 3, dt);
                                        const p4 = getLatestForState(st, 4, dt);
                                        return (
                                            <div key={dt} className="rounded p-1.5"
                                                 style={{ background: 'rgba(0,0,0,0.5)' }}>
                                                <div className="text-[9px] font-bold text-[#A0AAB2] uppercase mb-1 flex items-center gap-1">
                                                    <span>{dt === 'midday' ? '☀️' : '🌙'}</span>
                                                    {dt}
                                                </div>
                                                <ResultRow label="Pick 3" draw={p3} />
                                                <ResultRow label="Pick 4" draw={p4} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Main Game Area */}
                <div className="card-dark p-5 mb-4">
                    {/* State Selection — multi-select */}
                    <div className="mb-4">
                        <div className="flex items-center justify-center gap-1.5 mb-2 flex-wrap" data-testid="state-quickpicks">
                            <span className="text-[10px] uppercase font-bold text-[#A0AAB2] tracking-wide mr-1">Quick:</span>
                            <button
                                type="button"
                                onClick={() => setSelectedStates([...allowedStates])}
                                className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border transition-colors ${
                                    selectedStates.length === allowedStates.length
                                        ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                        : 'bg-white/5 text-[#A0AAB2] border-white/10 hover:text-white'
                                }`}
                                data-testid="state-quick-all"
                            >
                                {allowedStates.length === 1 ? allowedStates[0] : `All ${allowedStates.length}`}
                            </button>
                            {[
                                ['IL','MS'], ['IL','TN'], ['MS','TN'],
                            ].filter(pair => pair.every(s => allowedStates.includes(s))).map(pair => {
                                const id = pair.join('+');
                                const matches = selectedStates.length === 2 && pair.every(p => selectedStates.includes(p));
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => setSelectedStates([...pair])}
                                        className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border transition-colors ${
                                            matches
                                                ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                                : 'bg-white/5 text-[#A0AAB2] border-white/10 hover:text-white'
                                        }`}
                                        data-testid={`state-quick-${id}`}
                                    >
                                        {id}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex gap-2 justify-center">
                            {allowedStates.map(s => {
                                const isSelected = selectedStates.includes(s);
                                return (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => {
                                            setSelectedStates(prev => {
                                                const next = prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s];
                                                // Keep at least one selected
                                                if (next.length === 0) return [s];
                                                // Sync legacy single state to first selected (used by cutoff-time display)
                                                setSelectedState(next[0]);
                                                return next;
                                            });
                                        }}
                                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border-2 ${
                                            isSelected ? 'text-black' : 'bg-white/5 text-[#A0AAB2] hover:text-white border-transparent'
                                        }`}
                                        style={isSelected
                                            ? { backgroundColor: STATE_COLORS[s], borderColor: STATE_COLORS[s] }
                                            : {}
                                        }
                                        data-testid={`state-${s}`}
                                    >
                                        {s}
                                        {isSelected && (
                                            <span className="ml-1 text-[10px]" data-testid={`state-${s}-check`}>✓</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        {selectedStates.length > 1 && (
                            <p className="text-[10px] text-[#FFD700] text-center mt-1.5" data-testid="multi-state-hint">
                                {selectedStates.length} states × ${amount} = ${(parseFloat(amount || 0) * selectedStates.length).toFixed(2)} per drawing
                            </p>
                        )}
                    </div>

                    {/* Pick Type & Drawing Time */}
                    <div className="flex gap-2 justify-center mb-3">
                        {[3, 4].map(pt => (
                            <button key={pt} onClick={() => setPickType(pt)}
                                className={`px-5 py-2 rounded-lg text-sm font-black transition-all ${
                                    pickType === pt ? 'bg-[#FFD700] text-black' : 'bg-white/5 text-[#A0AAB2]'
                                }`} data-testid={`pick-${pt}`}>
                                Pick {pt}
                            </button>
                        ))}
                        <div className="border-l border-white/10 mx-1" />
                        {['midday', 'evening'].map(dt => (
                            <button key={dt} onClick={() => { setDrawingTime(dt); userPickedDrawRef.current = true; }}
                                className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                                    drawingTime === dt ? 'bg-white/20 text-white' : 'bg-white/5 text-[#A0AAB2]'
                                }`} data-testid={`time-${dt}`}>
                                {dt === 'midday' ? 'Midday' : 'Evening'}
                            </button>
                        ))}
                    </div>

                    {/* Numbers */}
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-white text-sm font-bold">Your Numbers</p>
                        <Button size="sm" variant="outline" onClick={quickPick}
                            className="border-[#FFD700]/30 text-[#FFD700] text-[10px] h-7" data-testid="quick-pick-btn">
                            Quick Pick
                        </Button>
                    </div>
                    <div className="flex gap-3 justify-center mb-4">
                        {numbers.map((n, i) => (
                            <select key={i} value={n === null ? '' : n} onChange={e => setNumber(i, e.target.value)}
                                className="w-14 h-14 rounded-xl bg-[#0a0a0a] border-2 border-[#FFD700]/30 text-[#FFD700] text-2xl font-black text-center appearance-none cursor-pointer focus:border-[#FFD700] focus:outline-none"
                                data-testid={`number-${i}`}>
                                <option value="" disabled>-</option>
                                {[0,1,2,3,4,5,6,7,8,9].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        ))}
                    </div>

                    {/* Bet Type */}
                    <div className="flex gap-2 justify-center mb-3 flex-wrap">
                        {(() => {
                            const ways = computeWays(numbers);
                            const filled = numbers.filter(n => n !== null && n !== undefined);
                            const allChosen = filled.length === numbers.length;
                            const comboAvailable = allChosen && ways > 1;
                            const chips = [
                                { id: 'straight',     label: 'Straight (Exact)',  active: 'bg-[#39FF14] text-black', enabled: true },
                                { id: 'box',          label: `Box${ways > 1 ? ` (${ways}-way)` : ''}`,   active: 'bg-[#A855F7] text-white', enabled: true },
                                { id: 'straight_box', label: 'Straight + Box',    active: 'bg-gradient-to-r from-[#39FF14] to-[#A855F7] text-black', enabled: true },
                                {
                                    id: 'ways',
                                    label: comboAvailable ? `Combo (${ways}-way)` : 'Combo',
                                    active: 'bg-gradient-to-r from-[#FFD700] to-[#FF007F] text-black',
                                    enabled: comboAvailable,
                                    disabledHint: allChosen
                                        ? `Combo needs ${pickType} different digits`
                                        : `Pick ${pickType} different digits first`,
                                },
                                {
                                    id: 'trips',
                                    label: pickType === 3 ? 'Trips' : 'Quads',
                                    active: 'bg-gradient-to-r from-[#FF6B35] to-[#FFD700] text-black',
                                    enabled: pickType === 3,
                                    disabledHint: 'Trips only available on Pick 3',
                                },
                            ];
                            return chips.map(({ id, label, active, enabled, disabledHint }) => (
                                <button key={id} type="button"
                                    onClick={() => { if (enabled) setBetType(id); }}
                                    disabled={!enabled}
                                    title={!enabled ? disabledHint : ''}
                                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                                        !enabled
                                            ? 'bg-white/5 text-[#555] opacity-50 cursor-not-allowed'
                                            : betType === id
                                                ? active
                                                : 'bg-white/5 text-[#A0AAB2]'
                                    }`} data-testid={`bet-type-${id}`}>
                                    {label}
                                </button>
                            ));
                        })()}
                    </div>
                    {betType === 'straight_box' && (
                        <p className="text-[10px] text-[#FFD700] text-center mb-3 -mt-1" data-testid="straight-box-hint">
                            Plays 2 bets per state (one straight, one box) — ${(parseFloat(amount || 0) * 2).toFixed(2)} per drawing
                        </p>
                    )}
                    {betType === 'ways' && (() => {
                        const ways = computeWays(numbers);
                        const amt = parseFloat(amount || 0);
                        const payouts = pickType === 3
                            ? (lotterySettings?.payouts_pick3 || { straight: 600, combo: 600 })
                            : (lotterySettings?.payouts_pick4 || { straight: 5000, combo: 5000 });
                        const comboRate = payouts.combo ?? payouts.straight;
                        const straightRate = payouts.straight;
                        const isDiscounted = comboRate < straightRate;
                        return (
                            <div className="mb-3 -mt-1 space-y-1" data-testid="ways-hint">
                                <p className="text-[10px] text-[#FFD700] text-center">
                                    Combo (written as <span className="font-mono">({numbers.filter(n => n !== null).join('')})</span> on paper) — plays {ways} straight tickets covering every order, ${(amt * ways).toFixed(2)} per state per drawing.
                                </p>
                                <p
                                    className={`text-[10px] text-center font-bold ${isDiscounted ? 'text-[#FF6B35]' : 'text-[#39FF14]'}`}
                                    data-testid="combo-rate-hint"
                                >
                                    Wins at the COMBO rate: <span className="font-mono">{comboRate}× wager</span>
                                    {isDiscounted
                                        ? <> (vs. straight {straightRate}× — lower payout in exchange for {ways}-way coverage)</>
                                        : <> — same as straight, but {ways}× more ways to hit</>}
                                </p>
                            </div>
                        );
                    })()}

                    {/* Trips inline sub-selector — single triple (0-9) or all 10 (000-999) */}
                    {betType === 'trips' && (
                        <div className="mb-3 -mt-1 rounded-lg border border-[#FF6B35]/30 bg-[#FF6B35]/5 p-3" data-testid="trips-sub-selector">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[10px] text-[#FFD700] font-bold uppercase tracking-widest">Triple to play</p>
                                <span className="text-[9px] text-[#A0AAB2]">
                                    {tripsDigit === null
                                        ? `All 10 triples (000–999) × ${Math.max(1, selectedStates.length)} state${selectedStates.length === 1 ? '' : 's'}`
                                        : `Only ${tripsDigit}${tripsDigit}${tripsDigit} × ${Math.max(1, selectedStates.length)} state${selectedStates.length === 1 ? '' : 's'}`}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                <button type="button" onClick={() => setTripsDigit(null)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all ${
                                        tripsDigit === null
                                            ? 'bg-gradient-to-r from-[#FF6B35] to-[#FFD700] text-black'
                                            : 'bg-white/5 text-[#A0AAB2] hover:bg-white/10'
                                    }`} data-testid="trips-digit-all">
                                    ALL
                                </button>
                                {[0,1,2,3,4,5,6,7,8,9].map(d => (
                                    <button key={d} type="button" onClick={() => setTripsDigit(d)}
                                        className={`w-10 h-9 rounded-lg text-sm font-black transition-all ${
                                            tripsDigit === d
                                                ? 'bg-[#FFD700] text-black'
                                                : 'bg-white/5 text-[#A0AAB2] hover:bg-white/10'
                                        }`} data-testid={`trips-digit-${d}`}>
                                        {d}{d}{d}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[10px] text-[#A0AAB2] mt-2">
                                Triples are stored as straight bets and pay at the admin-configurable <span className="text-[#FFD700] font-bold">Trips rate</span>. Ignores your selected numbers above.
                            </p>
                        </div>
                    )}

                    {/* Wager */}
                    <div className="flex items-center gap-2 justify-center mb-3">
                        <span className="text-xs text-[#A0AAB2]">Wager:</span>
                        {['0.50', '1', '2', '5', '10'].map(a => (
                            <button key={a} onClick={() => setAmount(a)}
                                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                                    amount === a ? 'bg-[#FFD700] text-black' : 'bg-white/5 text-[#A0AAB2]'
                                }`} data-testid={`amount-${a}`}>
                                ${a}
                            </button>
                        ))}
                    </div>

                    {/* To Win preview — sportsbook-style prominent card */}
                    {(() => {
                        const amt = parseFloat(amount || 0);
                        const numStates = Math.max(1, selectedStates.length);
                        const numDays = Math.max(1, selectedDates.length || 1);
                        const ways = betType === 'ways' ? Math.max(1, computeWays(numbers)) : 1;
                        // Trips: ALL = 10 tickets per state per draw; single digit = 1 ticket per state per draw
                        const tripsMul = betType === 'trips' ? (tripsDigit === null ? 10 : 1) : 1;
                        const { perDraw, total } = calcToWin(amt, pickType, betType, numStates, numDays, ways);
                        const hasWager = amt > 0;
                        const riskAmount = amt * numStates * numDays * ways * tripsMul;
                        return (
                            <div
                                className={`mb-4 rounded-xl border-2 ${hasWager
                                    ? 'border-[#39FF14]/50 bg-gradient-to-r from-[#39FF14]/10 to-[#FFD700]/5'
                                    : 'border-white/10 bg-[#0a0a0a]'} px-4 py-3 transition-colors`}
                                data-testid="to-win-preview"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-[10px] text-[#A0AAB2] uppercase tracking-widest font-bold">
                                            Risk
                                        </p>
                                        <p className="text-white text-lg font-black font-mono">
                                            ${riskAmount.toFixed(2)}
                                        </p>
                                        {(numStates > 1 || numDays > 1 || ways > 1 || tripsMul > 1) && (
                                            <p className="text-[10px] text-[#A0AAB2] -mt-0.5">
                                                {numStates}× state{numStates !== 1 ? 's' : ''} · {numDays}× day{numDays !== 1 ? 's' : ''}
                                                {ways > 1 && ` · ${ways}-way`}
                                                {tripsMul > 1 && ` · ${tripsMul}× triples`}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-[#A0AAB2] text-2xl font-light px-2">→</div>
                                    <div className="text-right">
                                        <p className="text-[10px] uppercase tracking-widest font-bold text-[#39FF14]">
                                            To Win
                                        </p>
                                        <p className={`text-2xl font-black font-mono ${hasWager ? 'text-[#39FF14]' : 'text-[#444]'}`}>
                                            ${perDraw.toFixed(2)}
                                        </p>
                                        <p className="text-[10px] text-[#A0AAB2] -mt-0.5">
                                            per matching drawing
                                        </p>
                                    </div>
                                </div>
                                {hasWager && (numStates > 1 || numDays > 1) && (
                                    <div className="mt-2 pt-2 border-t border-white/10 text-center">
                                        <span className="text-[10px] text-[#A0AAB2] uppercase tracking-wider">Max total potential </span>
                                        <span className="text-[#FFD700] font-mono font-black text-sm">${total.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Multi-Day Date Picker */}
                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs text-[#A0AAB2]">Play Dates <span className="text-[#666]">(up to 7 days)</span></p>
                            <button onClick={() => {
                                const today = new Date();
                                const week = [];
                                for (let i = 0; i < 7; i++) {
                                    const d = new Date(today); d.setDate(d.getDate() + i);
                                    week.push(d.toLocaleDateString('en-CA', { timeZone: 'America/Chicago' }));
                                }
                                setSelectedDates(week);
                            }} className="text-[10px] text-[#FFD700] hover:underline" data-testid="select-week-btn">
                                Select Full Week
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {Array.from({ length: 7 }, (_, i) => {
                                const d = new Date(); d.setDate(d.getDate() + i);
                                const dateStr = d.toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
                                const dayName = d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', weekday: 'short' });
                                const dayNum = d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric' });
                                const isSelected = selectedDates.includes(dateStr);
                                return (
                                    <button key={dateStr} onClick={() => {
                                        setSelectedDates(prev => isSelected ? prev.filter(x => x !== dateStr) : [...prev, dateStr]);
                                    }}
                                        className={`px-2 py-1.5 rounded-lg text-center transition-all min-w-[52px] ${
                                            isSelected ? 'bg-[#FFD700] text-black' : 'bg-white/5 text-[#A0AAB2] hover:bg-white/10'
                                        }`}
                                        data-testid={`date-${dateStr}`}>
                                        <span className="text-[10px] font-bold block">{dayName}</span>
                                        <span className="text-[9px] block">{dayNum}</span>
                                    </button>
                                );
                            })}
                        </div>
                        {selectedDates.length > 1 && (
                            <p className="text-[10px] text-[#FFD700] mt-1 text-center">
                                {selectedDates.length} days × ${amount} = ${(parseFloat(amount || 0) * selectedDates.length).toFixed(2)} total
                            </p>
                        )}
                    </div>

                    {/* Cutoff Time Display */}
                    {lotterySettings?.cutoffs && (
                        <div className="text-center mb-3">
                            <p className="text-[10px] text-[#FF3B30]">
                                Cutoff: {lotterySettings.cutoffs[`${selectedState}_${drawingTime}`] || 'N/A'} CT
                            </p>
                        </div>
                    )}

                    {/* Repeat last bet — one-tap restore */}
                    {lastSetup && editingIndex === null && (
                        <button
                            type="button"
                            onClick={repeatLastSetup}
                            className="w-full mb-2 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#A0AAB2] hover:text-white text-xs font-bold transition-colors flex items-center justify-center gap-2 border border-white/10 hover:border-[#FFD700]/40"
                            data-testid="repeat-last-setup-btn"
                            title="Restore your last bet setup"
                        >
                            ↻ Repeat Last Bet
                            <span className="text-[10px] text-[#666] font-normal">
                                {(lastSetup.states || []).join('+')} · {lastSetup.numbers?.join('-')} · {lastSetup.betType === 'straight_box' ? 'Str+Box' : lastSetup.betType} · ${lastSetup.amount}
                            </span>
                        </button>
                    )}

                    {/* Auto-buy toggle */}
                    {editingIndex === null && (
                        <div className="mb-3 p-3 rounded-lg bg-[#0a0a0a] border border-white/10" data-testid="autobuy-card">
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-xs font-bold text-white flex items-center gap-1.5">
                                        ⚡ Auto-buy {autobuy.enabled ? <span className="px-1.5 py-0.5 rounded text-[9px] bg-[#39FF14]/20 text-[#39FF14]">ON</span> : <span className="px-1.5 py-0.5 rounded text-[9px] bg-white/10 text-[#A0AAB2]">OFF</span>}
                                    </p>
                                    {autobuy.enabled && autobuy.config ? (
                                        <p className="text-[10px] text-[#A0AAB2] truncate mt-0.5">
                                            {(autobuy.config.states || []).join('+')} · {(autobuy.config.numbers || []).join('-')} · {autobuy.config.bet_type === 'straight_box' ? 'Str+Box' : autobuy.config.bet_type} · ${autobuy.config.amount} · {autobuy.config.drawing_time}
                                            {autobuy.last_run_date ? ` · last run ${autobuy.last_run_date}` : ' · scheduled'}
                                        </p>
                                    ) : (
                                        <p className="text-[10px] text-[#A0AAB2] mt-0.5">
                                            Plays your current setup automatically each day, 30 min before cutoff
                                        </p>
                                    )}
                                </div>
                                {autobuy.enabled ? (
                                    <button
                                        type="button"
                                        onClick={disableAutobuy}
                                        disabled={autobuyLoading}
                                        className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-50"
                                        data-testid="autobuy-disable-btn"
                                    >
                                        Turn Off
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={enableAutobuy}
                                        disabled={autobuyLoading || numbers.some(n => n === null)}
                                        className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-50"
                                        data-testid="autobuy-enable-btn"
                                    >
                                        Turn On
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Buy / Add to Slip */}
                    {betType === 'trips' ? (
                        <Button
                            onClick={placeInlineTrips}
                            disabled={loading || !parseFloat(amount) || parseFloat(amount) <= 0 || (selectedStates && selectedStates.length === 0)}
                            className="w-full bg-gradient-to-r from-[#FF6B35] to-[#FFD700] text-black font-black py-5 text-base rounded-xl"
                            data-testid="place-trips-btn"
                        >
                            <Ticket className="w-5 h-5 mr-2" />
                            {(() => {
                                const stateMul = (selectedStates && selectedStates.length > 0) ? selectedStates.length : 1;
                                const tripsMul = tripsDigit === null ? 10 : 1;
                                const dayMul = selectedDates.length > 0 ? selectedDates.length : 1;
                                const totalBets = stateMul * tripsMul * dayMul;
                                const totalCost = (parseFloat(amount || 0) * totalBets).toFixed(2);
                                const label = tripsDigit === null ? 'All Triples' : `Triple ${tripsDigit}${tripsDigit}${tripsDigit}`;
                                return loading ? 'Placing…' : `Place ${label} — $${totalCost}`;
                            })()}
                        </Button>
                    ) : (
                        <Button onClick={addToSlip} disabled={numbers.some(n => n === null)}
                            className="w-full bg-gradient-to-r from-[#FFD700] to-[#F4C430] text-black font-black py-5 text-base rounded-xl"
                            data-testid="add-to-slip-btn">
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            {(() => {
                                if (editingIndex !== null) return 'Update Bet';
                                const stateMul = (selectedStates && selectedStates.length > 0) ? selectedStates.length : 1;
                                const betMul = betType === 'straight_box' ? 2 : 1;
                                const dayMul = selectedDates.length > 0 ? selectedDates.length : 1;
                                const totalBets = stateMul * betMul * dayMul;
                                const totalCost = (parseFloat(amount || 0) * totalBets).toFixed(2);
                                if (totalBets === 1) return `Add to Slip — $${totalCost}`;
                                return `Add ${totalBets} bets — $${totalCost}`;
                            })()}
                        </Button>
                    )}
                </div>

                {/* Floating Bet Slip Button */}
                {betSlip.length > 0 && !showSlip && (
                    <motion.button
                        initial={{ scale: 0 }} animate={{ scale: 1 }}
                        onClick={() => setShowSlip(true)}
                        className="fixed bottom-6 right-6 bg-[#FFD700] text-black px-5 py-3 rounded-full font-black shadow-lg shadow-[#FFD700]/30 flex items-center gap-2 z-40"
                        data-testid="open-slip-btn"
                    >
                        <ShoppingCart className="w-5 h-5" />
                        Slip ({betSlip.length}) — ${getSlipTotal().toFixed(2)}
                    </motion.button>
                )}

                {/* Bet Slip Panel */}
                <AnimatePresence>
                    {showSlip && betSlip.length > 0 && (
                        <>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 z-[60]" onClick={() => setShowSlip(false)} />
                        <motion.div
                            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25 }}
                            className="fixed inset-x-0 bottom-0 z-[70] bg-[#121212] rounded-t-2xl border-t border-[#FFD700]/20 max-h-[80vh] overflow-hidden flex flex-col"
                            data-testid="bet-slip-panel"
                        >
                            {/* Slip Header */}
                            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
                                <h3 className="text-white font-black flex items-center gap-2">
                                    <ShoppingCart className="w-5 h-5 text-[#FFD700]" /> Bet Slip ({betSlip.length})
                                </h3>
                                <div className="flex items-center gap-3">
                                    <button onClick={clearSlip} className="text-xs text-[#FF3B30] hover:underline" data-testid="clear-slip-btn">Clear All</button>
                                    <button onClick={() => setShowSlip(false)} className="text-[#A0AAB2]"><X className="w-5 h-5" /></button>
                                </div>
                            </div>

                            {/* Slip Items */}
                            <div className="overflow-y-auto flex-1 px-4 py-3 space-y-2">
                                {betSlip.map((item, i) => {
                                    const days = item.dates?.length || 1;
                                    const itemTotal = item.amount * days;
                                    const { perDraw } = calcToWin(item.amount, item.pickType, item.betType, 1, 1);
                                    return (
                                        <div key={i} className="bg-[#0a0a0a] rounded-xl p-3 border border-white/5" data-testid={`slip-item-${i}`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ backgroundColor: STATE_COLORS[item.state] + '30', color: STATE_COLORS[item.state] }}>
                                                        {item.state}
                                                    </span>
                                                    <span className="text-xs text-[#A0AAB2]">Pick {item.pickType} • {item.drawingTime} • {item.betType}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <button onClick={() => editSlipItem(i)} className="p-1 text-[#FFD700] hover:bg-[#FFD700]/10 rounded" data-testid={`edit-slip-${i}`}>
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => removeSlipItem(i)} className="p-1 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded" data-testid={`remove-slip-${i}`}>
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="flex gap-1">
                                                    {item.numbers.map((n, j) => (
                                                        <span key={j} className="w-8 h-8 rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] text-sm font-bold flex items-center justify-center">{n}</span>
                                                    ))}
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-white font-mono font-bold">${itemTotal.toFixed(2)}</p>
                                                    {days > 1 && <p className="text-[10px] text-[#A0AAB2]">{days} days × ${item.amount}</p>}
                                                    <p className="text-[10px] text-[#39FF14] font-mono mt-0.5">To win <span className="font-black">${perDraw.toFixed(2)}</span></p>
                                                </div>
                                            </div>
                                            {item.dates && item.dates.length > 0 && (
                                                <p className="text-[9px] text-[#666] mt-1">{item.dates.join(', ')}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Slip Footer */}
                            <div className="px-5 py-4 border-t border-white/10 bg-[#0a0a0a]">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[#A0AAB2] text-sm">Total wager</span>
                                    <span className="text-xl font-black text-[#FFD700] font-mono">${getSlipTotal().toFixed(2)}</span>
                                </div>
                                {(() => {
                                    const maxWin = betSlip.reduce((sum, item) => {
                                        const days = item.dates?.length || 1;
                                        const { perDraw } = calcToWin(item.amount, item.pickType, item.betType, 1, 1);
                                        return sum + (perDraw * days);
                                    }, 0);
                                    return (
                                        <div className="flex items-center justify-between mb-3" data-testid="slip-max-win">
                                            <span className="text-[#A0AAB2] text-xs">Max possible win</span>
                                            <span className="text-base font-black text-[#39FF14] font-mono">${maxWin.toFixed(2)}</span>
                                        </div>
                                    );
                                })()}
                                <Button onClick={purchaseAll} disabled={purchaseLoading}
                                    className="w-full bg-gradient-to-r from-[#39FF14] to-[#00FF88] text-black font-black py-5 text-base rounded-xl"
                                    data-testid="purchase-all-btn">
                                    {purchaseLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                                        <>
                                            <Ticket className="w-5 h-5 mr-2" /> Purchase All — ${getSlipTotal().toFixed(2)}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </motion.div>
                        </>
                    )}
                </AnimatePresence>

                {/* Favorites */}
                {favorites.length > 0 && (
                    <div className="card-dark p-4 mb-3">
                        <p className="text-sm font-bold text-white flex items-center gap-1 mb-2"><Heart className="w-4 h-4 text-[#FF007F]" /> Saved Numbers</p>
                        <div className="flex flex-wrap gap-2">
                            {favorites.map((fav, i) => (
                                <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 border border-white/10">
                                    <button onClick={() => loadFavorite(fav)} className="flex gap-0.5" data-testid={`fav-${i}`}>
                                        {fav.numbers.map((n, j) => (
                                            <span key={j} className="w-5 h-5 rounded bg-[#FF007F]/20 text-[#FF007F] text-[10px] font-bold flex items-center justify-center">{n}</span>
                                        ))}
                                    </button>
                                    <button onClick={() => removeFavorite(i)} className="text-[#666] hover:text-white text-[10px] ml-1">×</button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* My Tickets */}
                <button ref={myTicketsRef} onClick={() => { setShowTickets(!showTickets); if (!showTickets) fetchTickets(); }}
                    className="w-full card-dark p-4 flex items-center justify-between mb-2" data-testid="my-tickets-toggle">
                    <span className="text-white font-bold flex items-center gap-2">
                        <History className="w-4 h-4" /> My Tickets
                        {pendingTicketCount > 0 && (
                            <span className="ml-1 px-2 py-0.5 rounded-full bg-[#FFD700] text-black text-[10px] font-black" data-testid="my-tickets-count">
                                {pendingTicketCount} PENDING
                            </span>
                        )}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-[#A0AAB2] transition-transform ${showTickets ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                    {showTickets && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                            {ticketsLoading ? (
                                <div className="py-8 text-center"><Loader2 className="w-5 h-5 text-[#FFD700] animate-spin mx-auto" /></div>
                            ) : myTickets.length === 0 ? (
                                <p className="text-[#A0AAB2] text-center py-6 text-sm">No tickets yet</p>
                            ) : (
                                <div className="space-y-2 mb-4">
                                    {myTickets.map(t => {
                                        const numbersArr = Array.isArray(t.numbers) ? t.numbers : null;
                                        const bulkLabel = !numbersArr
                                            ? (t.bet_type === 'trips' ? 'ALL TRIPLES' : t.bet_type === 'combo_all' ? 'ALL COMBOS' : String(t.numbers ?? ''))
                                            : null;
                                        const isEvening = (t.drawing_time || '').toLowerCase() === 'evening';
                                        return (
                                        <div key={t.id} className={`card-dark p-3 ${
                                            t.status === 'won' ? 'border-l-4 border-l-[#39FF14]' :
                                            t.status === 'lost' ? 'border-l-4 border-l-[#FF3B30]' : 'border-l-4 border-l-[#FFD700]'
                                        }`} data-testid={`ticket-${t.id}`}>
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-xs font-bold" style={{ color: STATE_COLORS[t.state] }}>{t.state}</span>
                                                    <span
                                                        className={`text-[9px] px-1.5 py-0.5 rounded-md font-black uppercase tracking-wider ${
                                                            isEvening
                                                                ? 'bg-[#1E3A8A]/40 text-[#93C5FD] border border-[#3B82F6]/40'
                                                                : 'bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30'
                                                        }`}
                                                        data-testid={`ticket-draw-time-${t.id}`}
                                                    >
                                                        {isEvening ? 'Evening' : 'Midday'}
                                                    </span>
                                                    <span className="text-xs text-[#A0AAB2]">
                                                        Pick {t.pick_type} •{' '}
                                                        {t.bet_type === 'trips'
                                                            ? <span className="text-[#FF6B35] font-black">TRIPS · {t.ways}-WAY</span>
                                                            : t.bet_type === 'combo_all'
                                                                ? <span className="text-[#FFD700] font-black">ALL COMBOS · {t.ways}-WAY</span>
                                                                : t.is_combo
                                                                    ? <span className="text-[#FFD700] font-black">COMBO · {t.ways}-WAY</span>
                                                                    : t.bet_type}
                                                    </span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                                                        t.status === 'won' ? 'bg-[#39FF14]/20 text-[#39FF14]' :
                                                        t.status === 'lost' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' : 'bg-[#FFD700]/20 text-[#FFD700]'
                                                    }`}>{t.status.toUpperCase()}</span>
                                                    {t.edited_at && (
                                                        <span className="text-[9px] text-[#A0AAB2] italic" title={`Edited ${new Date(t.edited_at).toLocaleString()}`}>edited</span>
                                                    )}
                                                </div>
                                                {isTicketEditable(t) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openEditTicket(t)}
                                                        className="text-[10px] flex items-center gap-1 px-2 py-1 rounded-md bg-[#FFD700]/15 border border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/25 transition-colors"
                                                        data-testid={`edit-ticket-${t.id}`}
                                                        title="Edit numbers before cutoff"
                                                    >
                                                        <Pencil className="w-3 h-3" />
                                                        Edit
                                                    </button>
                                                )}
                                                {isTicketEditable(t) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => cancelTicket(t)}
                                                        disabled={cancelingId === t.id}
                                                        className="text-[10px] flex items-center gap-1 px-2 py-1 rounded-md bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF6B6B] hover:bg-[#FF3B30]/25 transition-colors disabled:opacity-50"
                                                        data-testid={`cancel-ticket-${t.id}`}
                                                        title="Cancel & refund before cutoff"
                                                    >
                                                        <X className="w-3 h-3" />
                                                        {cancelingId === t.id ? 'Canceling…' : 'Cancel'}
                                                    </button>
                                                )}
                                                {numbersArr && (
                                                    <button
                                                        type="button"
                                                        onClick={() => replayTicket(t)}
                                                        disabled={replayingId === t.id}
                                                        className="text-[10px] flex items-center gap-1 px-2 py-1 rounded-md bg-[#39FF14]/15 border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/25 transition-colors disabled:opacity-50"
                                                        data-testid={`replay-ticket-${t.id}`}
                                                        title="Play these same numbers again for the next draw"
                                                    >
                                                        {replayingId === t.id
                                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                                            : <Zap className="w-3 h-3" />}
                                                        {replayingId === t.id ? 'Playing…' : 'Replay'}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="flex items-center justify-between">
                                                {numbersArr ? (
                                                    <div className="flex gap-1">
                                                        {numbersArr.map((n, i) => (
                                                            <span key={i} className="w-8 h-8 rounded-lg bg-[#0a0a0a] border border-[#FFD700]/30 text-[#FFD700] text-sm font-bold flex items-center justify-center">{n}</span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="inline-block px-3 py-1.5 rounded-lg bg-[#FF6B35]/15 text-[#FF6B35] text-xs font-black uppercase tracking-widest border border-[#FF6B35]/30">
                                                        {bulkLabel}
                                                    </span>
                                                )}
                                                <div className="text-right">
                                                    {t.status === 'won' ? (
                                                        <span className="text-[#39FF14] font-mono font-bold">+{Number(t.payout || 0).toFixed(2)}</span>
                                                    ) : t.status === 'lost' ? (
                                                        <span className="text-[#FF3B30] font-mono">-{Number((t.is_combo ? t.total_cost : t.amount) || 0).toFixed(2)}</span>
                                                    ) : (
                                                        <>
                                                            <span className="text-[#FFD700] font-mono block">${Number((t.is_combo ? t.total_cost : t.amount) || 0).toFixed(2)}</span>
                                                            {(() => {
                                                                const { perDraw } = calcToWin(t.amount, t.pick_type, t.bet_type, 1, 1);
                                                                return (
                                                                    <span className="text-[10px] text-[#39FF14] font-mono block mt-0.5">
                                                                        To win <span className="font-black">${perDraw.toFixed(2)}</span>
                                                                    </span>
                                                                );
                                                            })()}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            {t.winning_numbers && (
                                                <p className="text-[10px] text-[#A0AAB2] mt-1">Winning: {t.winning_numbers.join('-')}</p>
                                            )}
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Number Insights */}
                <button onClick={() => { setShowInsights(!showInsights); if (!showInsights) fetchInsights(); }}
                    className="w-full card-dark p-4 flex items-center justify-between mb-2" data-testid="insights-toggle">
                    <span className="text-white font-bold flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Hot & Cold Numbers</span>
                    <ChevronDown className={`w-4 h-4 text-[#A0AAB2] transition-transform ${showInsights ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                    {showInsights && insights && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-2">
                            <div className="card-dark p-4 space-y-3">
                                <div>
                                    <p className="text-xs font-bold text-[#FF3B30] flex items-center gap-1 mb-1"><Flame className="w-3 h-3" /> Hot Digits (Pick 3)</p>
                                    <div className="flex gap-2">
                                        {(insights.hot_pick3 || []).map((d, i) => (
                                            <span key={i} className="px-2 py-1 rounded bg-[#FF3B30]/20 text-[#FF3B30] text-xs font-bold">{d.digit} ({d.count})</span>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-[#00BFFF] flex items-center gap-1 mb-1"><Snowflake className="w-3 h-3" /> Cold Digits (Pick 3)</p>
                                    <div className="flex gap-2">
                                        {(insights.cold_pick3 || []).map((d, i) => (
                                            <span key={i} className="px-2 py-1 rounded bg-[#00BFFF]/20 text-[#00BFFF] text-xs font-bold">{d.digit} ({d.count})</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Leaderboard */}
                <button onClick={() => { setShowLeaderboard(!showLeaderboard); if (!showLeaderboard) fetchLeaderboard(); }}
                    className="w-full card-dark p-4 flex items-center justify-between mb-2" data-testid="leaderboard-toggle">
                    <span className="text-white font-bold flex items-center gap-2"><Trophy className="w-4 h-4 text-[#FFD700]" /> Top Winners</span>
                    <ChevronDown className={`w-4 h-4 text-[#A0AAB2] transition-transform ${showLeaderboard ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                    {showLeaderboard && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-4">
                            <div className="card-dark p-4">
                                {leaderboard.length === 0 ? (
                                    <p className="text-[#A0AAB2] text-center py-4 text-sm">No winners yet</p>
                                ) : leaderboard.map((p, i) => (
                                    <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                                i === 0 ? 'bg-[#FFD700] text-black' : i === 1 ? 'bg-[#C0C0C0] text-black' : i === 2 ? 'bg-[#CD7F32] text-white' : 'bg-white/10 text-[#A0AAB2]'
                                            }`}>{i + 1}</span>
                                            <span className="text-sm text-white">{p.name}</span>
                                        </div>
                                        <span className="text-sm font-mono text-[#39FF14]">{p.total_won.toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <p className="text-center text-[10px] text-[#666] mt-4 mb-2">For amusement only — fun credits, not real money.</p>

                {/* Rules & Regulations */}
                <button onClick={() => setShowRules(!showRules)}
                    className="w-full card-dark p-4 flex items-center justify-between mb-4" data-testid="rules-toggle">
                    <span className="text-white font-bold text-sm">Rules & Regulations</span>
                    <ChevronDown className={`w-4 h-4 text-[#A0AAB2] transition-transform ${showRules ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                    {showRules && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-8">
                            <div className="card-dark p-4">
                                <pre className="text-xs text-[#A0AAB2] whitespace-pre-wrap font-sans leading-relaxed">
                                    {lotterySettings?.rules || 'Loading rules...'}
                                </pre>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Edit-ticket modal (pre-cutoff numbers fix) */}
            <AnimatePresence>
                {editTicket && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
                        onClick={(e) => { if (e.target === e.currentTarget) setEditTicket(null); }}
                        data-testid="edit-ticket-modal"
                    >
                        <motion.div
                            initial={{ y: 30, scale: 0.96 }}
                            animate={{ y: 0, scale: 1 }}
                            exit={{ y: 30, scale: 0.96 }}
                            className="w-full sm:max-w-md card-dark border border-[#FFD700]/30 p-5 rounded-2xl"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                                    <Pencil className="w-4 h-4 text-[#FFD700]" /> Edit ticket
                                </h3>
                                <button onClick={() => setEditTicket(null)} className="text-[#A0AAB2] hover:text-white" data-testid="edit-ticket-close">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="text-xs text-[#A0AAB2] mb-1">
                                <span className="font-bold" style={{ color: STATE_COLORS[editTicket.state] }}>{editTicket.state}</span>
                                <span> · Pick {editTicket.pick_type} · {editTicket.drawing_time} · {editTicket.draw_date}</span>
                            </div>
                            <p className="text-[10px] text-[#A0AAB2] mb-4">
                                Original: {editTicket.numbers?.join('-')} ({editTicket.bet_type})
                                <span className="text-[#FFD700] ml-1">· locks at {lotterySettings?.cutoffs?.[`${editTicket.state}_${editTicket.drawing_time}`] || 'cutoff'} CT</span>
                            </p>

                            <label className="text-xs font-bold text-white block mb-2">New numbers</label>
                            <div className="flex gap-2 mb-4">
                                {Array.from({ length: editTicket.pick_type }).map((_, i) => (
                                    <input
                                        key={i}
                                        type="number"
                                        inputMode="numeric"
                                        min={0}
                                        max={9}
                                        value={editNumbers[i] ?? ''}
                                        onChange={(e) => setEditDigit(i, e.target.value)}
                                        className="w-14 h-14 text-center text-xl font-black bg-[#0a0a0a] border-2 border-[#FFD700]/40 text-[#FFD700] rounded-xl focus:border-[#FFD700] outline-none"
                                        data-testid={`edit-ticket-digit-${i}`}
                                    />
                                ))}
                            </div>

                            <label className="text-xs font-bold text-white block mb-2">Bet type</label>
                            <div className="grid grid-cols-2 gap-2 mb-3">
                                {['straight', 'box'].map((bt) => (
                                    <button
                                        key={bt}
                                        type="button"
                                        onClick={() => setEditBetType(bt)}
                                        className={`py-2 rounded-lg text-xs font-bold uppercase tracking-wide border transition-colors ${
                                            editBetType === bt
                                                ? 'bg-[#FFD700] border-[#FFD700] text-black'
                                                : 'bg-[#0a0a0a] border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10'
                                        }`}
                                        data-testid={`edit-ticket-bet-${bt}`}
                                    >
                                        {bt}
                                    </button>
                                ))}
                            </div>

                            {/* To Win preview (uses the ticket's locked wager amount) */}
                            {(() => {
                                const { perDraw } = calcToWin(editTicket.amount, editTicket.pick_type, editBetType, 1, 1);
                                return (
                                    <div className="mb-5 rounded-lg border-2 border-[#39FF14]/50 bg-gradient-to-r from-[#39FF14]/10 to-[#FFD700]/5 px-4 py-2.5 flex items-center justify-between"
                                         data-testid="edit-ticket-to-win">
                                        <div>
                                            <p className="text-[10px] text-[#A0AAB2] uppercase tracking-widest font-bold">Risk</p>
                                            <p className="text-white text-base font-black font-mono">${Number(editTicket.amount).toFixed(2)}</p>
                                        </div>
                                        <span className="text-[#A0AAB2] text-xl px-2">→</span>
                                        <div className="text-right">
                                            <p className="text-[10px] text-[#39FF14] uppercase tracking-widest font-bold">To Win</p>
                                            <p className="text-[#39FF14] text-xl font-black font-mono">${perDraw.toFixed(2)}</p>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setEditTicket(null)}
                                    className="flex-1 border-[#FFD700]/30 bg-transparent text-[#A0AAB2] hover:bg-[#FFD700]/10"
                                    data-testid="edit-ticket-cancel"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={submitEditTicket}
                                    disabled={editSaving}
                                    className="flex-1 bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-bold"
                                    data-testid="edit-ticket-save"
                                >
                                    {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Lottery;
